import React from "react";
import { Box, Button, Tooltip, alpha, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { PlayArrow, Share as ShareIcon } from "@mui/icons-material";
import SelectedVideosList from "../SelectedVideosList";
import VideoUploader from "../VideoUploader";
import { VideoPreview } from "../../../shared/interfaces/VideoPreview";

interface SelectionControlsProps {
  selectedVideos: string[];
  allVideos: VideoPreview[];
  onRemoveVideo: (id: string) => void;
  onClearSelection: () => void;
  onProcessVideos: () => void;
  fetchVideos: () => void;
  allowUpload: boolean;
  /** Opens the share dialog for a single selected video. */
  onShareVideo?: (id: string) => void;
  /** Opens the share dialog for every shareable selected video at once. */
  onShareAll?: () => void;
  /** Gates the per-video share affordance. */
  canShareVideo?: (id: string) => boolean;
  /** How many of the selected videos the user may share; drives the
   *  "Share all" button's enabled state and label. */
  shareableCount?: number;
}

export const SelectionControls: React.FC<SelectionControlsProps> = ({
  selectedVideos,
  allVideos,
  onRemoveVideo,
  onClearSelection,
  onProcessVideos,
  fetchVideos,
  allowUpload,
  onShareVideo,
  onShareAll,
  canShareVideo,
  shareableCount = 0,
}) => {
  const theme = useTheme();

  return (
    <Grid size={{ xs: 12 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Display list of currently selected videos. The clear-selection
            action lives inside this box (its header), next to the count. */}
        <SelectedVideosList
          selectedVideos={selectedVideos}
          videos={allVideos}
          onRemove={onRemoveVideo}
          onClear={onClearSelection}
          onShareVideo={onShareVideo}
          canShareVideo={canShareVideo}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Video upload component - only enabled for KARDA */}
            <VideoUploader
              fetchVideos={fetchVideos}
              allowUpload={allowUpload}
            />
          </Box>
          {selectedVideos.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {onShareAll && (
              <Tooltip
                title={
                  shareableCount === 0
                    ? "You don't have permission to share the selected videos"
                    : `Share ${
                        shareableCount === 1
                          ? "the selected video"
                          : shareableCount < selectedVideos.length
                            ? `${shareableCount} shareable videos`
                            : `all ${shareableCount} selected videos`
                      }`
                }
                arrow
              >
                <span style={{ width: "100%" }}>
                  <Button
                    variant="outlined"
                    disabled={shareableCount === 0}
                    onClick={onShareAll}
                    startIcon={<ShareIcon />}
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      height: 48,
                      px: 3,
                      fontWeight: 600,
                      fontSize: "1rem",
                      borderRadius: 2,
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    Share
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip
              title={
                selectedVideos.length === 0
                  ? "Select videos to continue"
                  : "Proceed to review selected videos"
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  disabled={selectedVideos.length === 0}
                  onClick={onProcessVideos}
                  startIcon={<PlayArrow />}
                  sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  height: 48,
                  px: 4,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  background:
                    selectedVideos.length === 0
                      ? undefined
                      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow:
                    selectedVideos.length === 0
                      ? undefined
                      : `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.5)}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: `0 2px 8px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                  "&:focus-visible": {
                    outline: `3px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                    outlineOffset: 2,
                  },
                  "&.Mui-disabled": {
                    background: undefined,
                    boxShadow: "none",
                  },
                }}
              >
                View Selected Videos
              </Button>
            </span>
          </Tooltip>
          </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );
};
